import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Download, 
  FileText, 
  Calculator, 
  Users, 
  BookOpen,
  CheckCircle
} from "lucide-react";

export const Resources = () => {
  const resources = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "PAC Partnership Guide",
      description: "Comprehensive guide explaining the legal framework, benefits, and implementation process for PACs.",
      type: "PDF Guide",
      color: "bg-blue-100 text-blue-600",
      size: "2.3 MB"
    },
    {
      icon: <Calculator className="w-6 h-6" />,
      title: "Financial Benefits Calculator",
      description: "Excel spreadsheet to calculate potential funding based on your school's specific parameters.",
      type: "Excel Tool",
      color: "bg-green-100 text-green-600",
      size: "1.8 MB"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Sample MOU Template",
      description: "Template Memorandum of Understanding between districts and PACs for benefit allocation.",
      type: "Word Document",
      color: "bg-purple-100 text-purple-600",
      size: "0.5 MB"
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Implementation Checklist",
      description: "Step-by-step checklist for PACs to navigate the partnership process from start to finish.",
      type: "PDF Checklist",
      color: "bg-orange-100 text-orange-600",
      size: "0.8 MB"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Compliance Framework",
      description: "Detailed documentation of legal compliance requirements and regulatory considerations.",
      type: "PDF Document",
      color: "bg-red-100 text-red-600",
      size: "1.2 MB"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Case Study Collection",
      description: "Real-world examples of successful PAC partnerships and their outcomes.",
      type: "PDF Report",
      color: "bg-indigo-100 text-indigo-600",
      size: "3.1 MB"
    }
  ];

  const handleDownload = (resourceTitle: string) => {
    // In a real implementation, this would trigger actual file downloads
    alert(`Downloading: ${resourceTitle}\n\nNote: This is a demo. In the actual implementation, this would download the requested resource.`);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-green-600 border-green-600">
            <Download className="w-4 h-4 mr-2" />
            Downloadable Resources
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Tools & Documentation
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Access comprehensive resources to help your PAC understand, evaluate, 
            and implement the GreenSmart energy partnership program.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {resources.map((resource, index) => (
            <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardHeader className="pb-4">
                <div className={`w-12 h-12 rounded-xl ${resource.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {resource.icon}
                </div>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="secondary" className="text-xs">
                    {resource.type}
                  </Badge>
                  <span className="text-xs text-gray-500">{resource.size}</span>
                </div>
                <CardTitle className="text-lg font-semibold text-gray-900">
                  {resource.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {resource.description}
                </p>
                <Button 
                  onClick={() => handleDownload(resource.title)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Need Additional Support?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Our team provides personalized guidance throughout the partnership process. 
              From initial evaluation to implementation and ongoing support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Schedule Consultation
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                Request Custom Resources
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};